import Analytics from '@components/charts/analytics/Analytics'
import LastTransactionsTable from '@components/charts/membership/analytics-overview/LastTransactionsTable'
import ClubToday from '@components/charts/membership/revenue/ClubToday'
import MonthlyRevenueChart from '@components/charts/membership/revenue/MonthlyRevenueChart'
import NeedsAttention from '@components/charts/membership/revenue/NeedsAttention'
import PeriodTiles from '@components/charts/membership/revenue/PeriodTiles'
import PromotionDiscounts from '@components/charts/membership/revenue/PromotionDiscounts'
import RenewalRetention from '@components/charts/membership/revenue/RenewalRetention'
import SectionHead from '@components/charts/membership/revenue/SectionHead'
import TableSection from '@components/charts/membership/revenue/TableSection'
import UpcomingRenewals from '@components/charts/membership/revenue/UpcomingRenewals'
import useRevenueLinks from '@components/charts/membership/revenue/useRevenueLinks'
import useRevenueStatistics from '@components/charts/membership/revenue/useRevenueStatistics'
import Page from '@components/Page/Page'
import Feature from '@components/Feature'
import { AnalyticsProvider } from '@contexts/AnalyticsContext'
import {
  ResponseMembershipAttentionAnalyticsStatistics,
  ResponseMembershipPeriodAnalyticsStatistics,
} from '@models/membership/MembershipAnalyticsStatistics'
import { useAppSelector } from '@store/hooks'
import { selectPrograms } from '@store/membership/ProgramReducer'
import {
  analyticsRangePresets,
  lastTwelveMonthsRange,
} from '@utils/membershipRangePresets'
import { Permission } from '@utils/permissions'
import { Col, Divider, Empty, Flex } from 'antd'
import dayjs from 'dayjs'
import { useMemo, useRef } from 'react'

/** Part B: everything that follows the date range. */
const OverThePeriod = () => {
  const rangePresets = useMemo(() => analyticsRangePresets(), [])
  const defaultRange = useMemo(() => lastTwelveMonthsRange(), [])
  const link = useRevenueLinks()
  const { data, isLoading, error } =
    useRevenueStatistics<ResponseMembershipPeriodAnalyticsStatistics>(
      'membership-period',
      true
    )

  return (
    <Flex vertical gap={32}>
      <Flex vertical gap={24}>
        <SectionHead
          title="Over the period"
          caption="Change vs the previous period of the same length"
          extra={
            <div style={{ width: 240 }}>
              <Analytics.Date
                rangePresets={rangePresets}
                defaultValue={defaultRange}
                maxDate={dayjs()}
              />
            </div>
          }
        />
        <PeriodTiles data={data} isLoading={isLoading} error={error} />
      </Flex>
      <MonthlyRevenueChart currency={data?.currency} />
      <RenewalRetention />
      <PromotionDiscounts />
      <TableSection
        title="Last transactions"
        caption="Preview · 5 most recent transactions"
        linkTo={link('/membership/revenue/transactions')}
        linkLabel="View all transactions"
      >
        <LastTransactionsTable />
      </TableSection>
    </Flex>
  )
}

const RevenueContent = () => {
  // `membership-attention` once: Needs attention and Upcoming renewals (currency).
  const attention =
    useRevenueStatistics<ResponseMembershipAttentionAnalyticsStatistics>(
      'membership-attention',
      false
    )

  // No program at all: the program scope never resolves, so say so instead of
  // leaving every block on its skeleton. Only after the program list has loaded.
  const programs = useAppSelector(selectPrograms())
  const { isLoading: isProgramsLoading } = useAppSelector(
    (state) => state.program
  )
  const sawProgramsLoading = useRef(false)
  if (isProgramsLoading) sawProgramsLoading.current = true
  const hasNoProgram =
    sawProgramsLoading.current && !isProgramsLoading && programs.length === 0

  return (
    <Page
      permissions={Permission.viewRevenueMembership}
      noWrapWithCol
      pageTitle={{
        title: 'Revenue',
        level: 4,
        toolbar: (
          <div style={{ width: 150 }}>
            <Analytics.MembershipProgram />
          </div>
        ),
      }}
    >
      {hasNoProgram ? (
        <Col span={24}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No membership program yet: revenue appears once a program exists."
          />
        </Col>
      ) : (
        <Col span={24}>
          {/* Revenue is entirely a flag preview: the whole content is framed. */}
          <Feature flag="membershipAnalyticsOverview" preview="section">
            <Flex vertical gap={32}>
              {/* Part A: a snapshot of today, no date range. */}
              <NeedsAttention attention={attention} />
              <ClubToday />
              <UpcomingRenewals currency={attention.data?.currency} />
              <Divider style={{ margin: 0 }} />
              {/* Part B: follows the date range. */}
              <OverThePeriod />
            </Flex>
          </Feature>
        </Col>
      )}
    </Page>
  )
}

const RevenuePage = () => (
  <AnalyticsProvider membershipProgramScoped>
    <RevenueContent />
  </AnalyticsProvider>
)

export default RevenuePage
